import { TEMPLATE_IMAGE_ID_ATTR } from './templateImageAttrs.ts';

// Cheap guard so variable-only templates (the vast majority) never get
// DOM-parsed/re-serialized — keeps their wire format byte-identical.
const IMAGE_HELPER_PRESENT = /\{\{\s*image\s/;

// Matches a full `src` value of `{{image '<uuid>'}}` (single or double quotes),
// the wire format the backend stores. Must mirror the backend IMAGE_REF.
const IMAGE_SRC_PATTERN =
  /^\s*\{\{\s*image\s+['"]([0-9a-fA-F-]{36})['"]\s*}}\s*$/;

// True when the html contains an `{{image ...}}` helper. Lets the in-converter
// skip the DOM round-trip for the (common) image-free template.
export const hasImageHelper = (html: string): boolean =>
  IMAGE_HELPER_PRESENT.test(html);

// Rewrites stored `<img src="{{image '<id>'}}">` into an editor-renderable
// `<img src="<signed url>" data-template-image-id="<id>">`. The id is always
// preserved (even when the url can't be resolved — deleted/expired image) so a
// later save roundtrips the `{{image}}` reference back intact.
export const resolveImagePreviews = (
  html: string,
  resolveImageUrl: (fileId: string) => string | undefined,
): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.body.querySelectorAll('img').forEach((img) => {
    const match = (img.getAttribute('src') ?? '').match(IMAGE_SRC_PATTERN);
    if (!match) return;

    const [, fileId] = match;
    img.setAttribute(TEMPLATE_IMAGE_ID_ATTR, fileId);

    const url = resolveImageUrl(fileId);
    if (url) {
      img.setAttribute('src', url);
    } else {
      img.removeAttribute('src');
    }
  });

  return doc.body.innerHTML;
};

// Restore the backend image reference: the editor holds a signed preview URL
// in `src`; on save we swap it back to `{{image '<id>'}}` and drop the
// preview-only attributes so only the stable reference is persisted.
export const restoreImageRefs = (doc: Document): void => {
  doc.body.querySelectorAll(`img[${TEMPLATE_IMAGE_ID_ATTR}]`).forEach((img) => {
    const fileId = img.getAttribute(TEMPLATE_IMAGE_ID_ATTR);
    if (!fileId) {
      img.remove();
      return;
    }
    img.setAttribute('src', `{{image '${fileId}'}}`);
    img.removeAttribute(TEMPLATE_IMAGE_ID_ATTR);
    img.removeAttribute('class');
  });
};
